// Cookie Manager Utility
export class CookieManager {
  static CONSENT_KEY = 'cosmo-cookie-consent';
  static CONSENT_VERSION = '1.0';

  // Controlla se il consenso è stato dato
  static hasConsent() {
    try {
      const consent = localStorage.getItem(this.CONSENT_KEY);
      return consent !== null;
    } catch (e) {
      return false;
    }
  }

  // Ottiene le preferenze correnti
  static getPreferences() {
    try {
      const consent = localStorage.getItem(this.CONSENT_KEY);
      if (consent) {
        const parsed = JSON.parse(consent);
        console.log('📋 Cookie Preferences Retrieved:', parsed);
        return parsed;
      }
    } catch (e) {
      console.error('Error parsing cookie preferences:', e);
    }
    
    // ✅ DEFAULT: Abilita functionality per permettere HeyGen di funzionare
    return {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: true, // ✅ ABILITATO DI DEFAULT per HeyGen
      functionality: true // ✅ AGGIUNTO: Alias per preferences
    };
  }

  // ✅ FIXED: Controlla se una specifica categoria è abilitata con logging migliorato
  static isEnabled(category) {
    const preferences = this.getPreferences();
    
    // Log dettagliato per debugging
    console.log(`🍪 Cookie Check - Category: ${category}`);
    console.log(`🍪 Cookie Check - All Preferences:`, preferences);
    
    // SPECIAL CASE: Se la categoria è 'functionality', controlla anche 'preferences'
    if (category === 'functionality') {
      const result = preferences.preferences || preferences.functionality || true; // ✅ DEFAULT true
      console.log(`🍪 Cookie Check - Functionality Result: ${result}`);
      return result;
    }
    
    const result = preferences[category] || false;
    console.log(`🍪 Cookie Check - Result: ${result}`);
    return result;
  }

  // Salva le preferenze
  static savePreferences(preferences) {
    const consentData = {
      ...preferences,
      essential: true, // Sempre abilitato
      timestamp: new Date().toISOString(),
      version: this.CONSENT_VERSION
    };

    try {
      localStorage.setItem(this.CONSENT_KEY, JSON.stringify(consentData));
      console.log('💾 Cookie Preferences Saved:', consentData);
      this.updateGoogleConsent(preferences);
      return true;
    } catch (e) {
      console.error('Error saving cookie preferences:', e);
      return false;
    }
  }

  // Aggiorna il consenso per Google Analytics/Tag Manager
  static updateGoogleConsent(preferences) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'functionality_storage': preferences.preferences ? 'granted' : 'denied',
        'personalization_storage': preferences.preferences ? 'granted' : 'denied'
      });
    }
  }

  // Revoca tutto il consenso
  static revokeConsent() {
    try {
      localStorage.removeItem(this.CONSENT_KEY);
      this.updateGoogleConsent({
        analytics: false,
        marketing: false,
        preferences: false
      });
      return true;
    } catch (e) {
      console.error('Error revoking consent:', e);
      return false;
    }
  }

  // Carica condizionalmente script di terze parti
  static loadScript(src, category = 'analytics') {
    if (!this.isEnabled(category)) {
      console.log(`Script ${src} not loaded - ${category} cookies not enabled`);
      return false;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // Inizializza Google Analytics condizionalmente
  static initializeGoogleAnalytics(measurementId) {
    if (!this.isEnabled('analytics')) {
      console.log('Google Analytics not initialized - analytics cookies not enabled');
      return;
    }

    // Carica Google Analytics
    this.loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, 'analytics')
      .then(() => {
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', measurementId, {
          'anonymize_ip': true,
          'cookie_flags': 'SameSite=None;Secure'
        });
        console.log('Google Analytics initialized');
      })
      .catch(error => {
        console.error('Error loading Google Analytics:', error);
      });
  }

  // Inizializza Google Tag Manager condizionalmente
  static initializeGoogleTagManager(containerId) {
    if (!this.isEnabled('marketing')) {
      console.log('Google Tag Manager not initialized - marketing cookies not enabled');
      return;
    }

    // Carica Google Tag Manager
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(script);

    // Aggiungi noscript tag per GTM
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.appendChild(noscript);

    console.log('Google Tag Manager initialized');
  }

  // Pulisce tutti i cookie non essenziali
  static clearNonEssentialCookies() {
    const preferences = this.getPreferences();

    // Lista dei cookie da rimuovere se non consentiti
    const cookiesToCheck = [
      // Google Analytics
      { name: '_ga', category: 'analytics' },
      { name: '_ga_', category: 'analytics' },
      { name: '_gid', category: 'analytics' },
      { name: '_gat', category: 'analytics' },
      // Google Tag Manager
      { name: '_gtm', category: 'marketing' },
      // HeyGen
      { name: 'heygen_', category: 'preferences' }
    ];

    cookiesToCheck.forEach(({ name, category }) => {
      if (!preferences[category]) {
        this.deleteCookie(name);
      }
    });
  }

  // Elimina un cookie specifico
  static deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${window.location.hostname};`;
  }

  // Verifica se deve mostrare il banner
  static shouldShowBanner() {
    return !this.hasConsent();
  }
}

export default CookieManager;